import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { verifyRules } from 'src/utils/functions';
import useAuth from 'src/hooks/useAuth';
import Iconify from 'src/components/Iconify';

import v2_actionConfigCrm from 'src/actions/v2/crm/v2_actionConfigCrm';
import getActionListaUsuariosByClienteId from 'src/actions/usuarios/getActionListaUsuariosByClienteId';
import v2_actionListaTagsChats from 'src/actions/v2/chats/v2_actionListaTagsChats';
import v2_action_crm_leadsPorUsuarios from 'src/actions/v2/dashboard/relatorios/v2_action_crm_leadsPorUsuarios';
import v2_actionInsightsExecutivo from 'src/actions/v2/dashboard/relatorios/v2_actionInsightsExecutivo';
import v2_actionInsightsComercial from 'src/actions/v2/dashboard/relatorios/v2_actionInsightsComercial';
import v2_actionInsightsPerformance from 'src/actions/v2/dashboard/relatorios/v2_actionInsightsPerformance';
import v2_actionGerarExcelRelatorioNegocios from 'src/actions/v2/dashboard/relatorios/excel/v2_actionGerarExcelRelatorioNegocios';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from 'src/@/components/ui/popover';
import { MultiSelect } from 'src/@/components/ui/multi-select';

import { PAINEL_DESIGN as D, T } from 'src/pages/dashboard/Painel/Painel.design';
import {
    BarRow,
    Block,
    ColumnChart,
    ConversionFunnel,
    Dash,
    Empty,
    KpiCard,
    Legend,
    PageHead,
    SkeletonBar,
    Stacked,
    StatStrip,
    Strong,
    fmtBRL,
    fmtInt,
    fmtPct,
    ratio,
} from './metricas/MetricasUI';

// ----------------------------------------------------------------------
// Aba "Funil" — etapas, conversão, aging e resultado do pipeline comercial.
//
// "Etapas do funil" e "Conversão por estágio" mostram a visão gráfica de funil
// (segmentos que afinam com o volume, conector com a conversão para a etapa
// seguinte); a tabela detalhada com aging fica logo abaixo — o gráfico dá a
// leitura rápida, a tabela dá o número exato por etapa.
//
// Também é usada em Marketing > Métricas da campanha (`campaingConfig`), onde
// o período vem da campanha em vez do filtro global.
// ----------------------------------------------------------------------

// Tabela ocupa a largura toda do card: nome e barra de volume levam o espaço,
// Conv./Aging ficam estreitas (são números curtos alinhados à direita).
const COLS = 'minmax(160px, 1.6fr) minmax(220px, 4fr) 90px 90px';
const CORES_ETAPA = [T.brand, T.brand2, '#8b5cf6', T.brand3, T.brand4, '#ddd6fe'];

// O backend formata ano_mes como "%m-%Y" (ex.: "07-2026"), não "YYYY-MM" —
// e ordena por essa string, o que quebra a cronologia ao cruzar o ano novo
// (ex.: "01-2026" < "07-2025" lexicamente). Por isso parseamos e ordenamos
// de novo aqui em vez de confiar na ordem que a API devolve.
function parseAnoMes(anoMes) {
    return moment(anoMes, 'MM-YYYY', true);
}

function rotuloAnoMes(anoMes) {
    const m = parseAnoMes(anoMes);
    return m.isValid() ? `${m.format('MMM')}/${m.format('YY')}` : String(anoMes || '');
}

export default function TabCrm({ periodo, inicio, fim, campaingConfig, hidePannels }) {
    const { user, cliente } = useAuth();
    const podeVerTodosLeads = verifyRules(user.rules, 'supervisionar_leads') || verifyRules(user.rules, 'negocios.verTodos');

    // O CRM é sempre do cliente principal: os funis, estágios e leads vivem no
    // cliente-raiz, não nos canais filhos. Por isso esta aba ignora o filtro de
    // canal do topo (que também fica oculto enquanto ela está ativa).
    const crmClienteId = cliente._id;

    const dataInicio = campaingConfig?.start_date || inicio || moment().startOf('month').format('YYYY-MM-DD');
    const dataFim = campaingConfig?.end_date || fim || moment().endOf('month').format('YYYY-MM-DD');
    const periodoEfetivo = campaingConfig?.periodo ? 'null' : periodo;

    const [pipelineId, setPipelineId] = useState('');
    const [configCrm, setConfigCrm] = useState({});

    const [loading, setLoading] = useState(true);
    const [loadingComercial, setLoadingComercial] = useState(true);
    const [loadingExportExcel, setLoadingExportExcel] = useState(false);

    const [origens, setOrigens] = useState([]);
    const [leadsPorUsuario, setLeadsPorUsuario] = useState([]);
    const [ganhosPerdas, setGanhosPerdas] = useState([]);
    const [motivosPerdas, setMotivosPerdas] = useState([]);
    const [conversaoEstagios, setConversaoEstagios] = useState([]);
    const [negociosUsuarios, setNegociosUsuarios] = useState([]);
    const [totais, setTotais] = useState(null);
    const [comercial, setComercial] = useState(null);
    const [executivo, setExecutivo] = useState(null);
    const [conversaoPorAgente, setConversaoPorAgente] = useState([]);

    // Filtros próprios da aba (o período vem do topo da página)
    const [qualificacao, setQualificacao] = useState('all');
    const [responsaveis, setResponsaveis] = useState('me');
    const [usuarioSelected, setUsuarioSelected] = useState(null);
    const [chatTags, setChatTags] = useState([]);
    const [listaUsuarios, setListaUsuarios] = useState([]);
    const [listaChatTags, setListaChatTags] = useState([]);

    const filtrosAtivos =
        (qualificacao !== 'all' ? 1 : 0) + (usuarioSelected !== null ? 1 : 0) + (chatTags.length > 0 ? 1 : 0);

    function montarQuerys(pid) {
        const tagsParam = chatTags.length > 0 ? chatTags.join(',') : '';
        const userIdParam = usuarioSelected || user.id;
        let querys = `?pipeline_id=${pid}&usuario_id=${userIdParam}&cliente_id=${crmClienteId}`;
        querys += `&inicio=${dataInicio}&final=${dataFim}&periodo=${periodoEfetivo}`;
        querys += `&qualificacao=${qualificacao}&responsaveis=${responsaveis}&chat_tags=${tagsParam}`;
        if (campaingConfig) querys += `&campaign_id=${campaingConfig.campaign_id}`;
        return querys;
    }

    async function carregar(pid) {
        if (!pid) return;
        const querys = montarQuerys(pid);
        setLoading(true);
        setLoadingComercial(true);
        const [resLeads, resExec, resComercial, resPerf] = await Promise.allSettled([
            v2_action_crm_leadsPorUsuarios(querys),
            v2_actionInsightsExecutivo(querys),
            v2_actionInsightsComercial(querys),
            v2_actionInsightsPerformance(querys),
        ]);
        if (resLeads.status === 'fulfilled') {
            const d = resLeads.value.data.data;
            setOrigens(d.origens || []);
            setLeadsPorUsuario(d.leads_usuarios || []);
            setGanhosPerdas(d.ganhosPerdas || []);
            setMotivosPerdas(d.motivosPerdas || []);
            setConversaoEstagios(d.conversaoEstagios || []);
            setNegociosUsuarios(d.negociosUsuarios || []);
            setTotais(d.totais || null);
        }
        if (resExec.status === 'fulfilled') setExecutivo(resExec.value.data.data);
        if (resComercial.status === 'fulfilled') setComercial(resComercial.value.data.data);
        if (resPerf.status === 'fulfilled') setConversaoPorAgente(resPerf.value.data.data?.conversaoPorAgente || []);
        setLoading(false);
        setLoadingComercial(false);
    }

    // Config do CRM: define o funil padrão e dispara a primeira carga.
    useEffect(() => {
        let ativo = true;
        (async () => {
            try {
                const result = await v2_actionConfigCrm(crmClienteId);
                if (!ativo || !result.data.data) return;
                setConfigCrm(result.data.data);
                const pipelines = result.data.data.pipelines || [];
                if (pipelines.length > 0) {
                    const pid = pipelineId || pipelines[0]._id;
                    setPipelineId(pid);
                    carregar(pid);
                }
            } catch (error) {
                console.log('err buscarConfigCrm', error);
            }
        })();
        return () => {
            ativo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodo, dataInicio, dataFim]);

    useEffect(() => {
        (async () => {
            try {
                const [resUsuarios, resTags] = await Promise.allSettled([
                    getActionListaUsuariosByClienteId(crmClienteId),
                    v2_actionListaTagsChats(`?cliente_id=${crmClienteId}`),
                ]);
                if (resUsuarios.status === 'fulfilled') setListaUsuarios(resUsuarios.value.data.usuarios || []);
                if (resTags.status === 'fulfilled') setListaChatTags(resTags.value.data?.data || []);
            } catch (error) {
                console.log(error);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (pipelineId) carregar(pipelineId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qualificacao, responsaveis, usuarioSelected, chatTags]);

    async function exportarExcel() {
        try {
            setLoadingExportExcel(true);
            const tagsParam = chatTags.length > 0 ? chatTags.join(',') : '';
            const userIdParam = usuarioSelected || user.id;
            const querys = `?periodo=${periodoEfetivo}&usuario_id=${userIdParam}&cliente_id=${crmClienteId}&data_inicial=${dataInicio}&data_final=${dataFim}&pipeline_id=${pipelineId}&qualificacao=${qualificacao}&responsaveis=${responsaveis}&chat_tags=${tagsParam}`;
            const response = await v2_actionGerarExcelRelatorioNegocios(querys);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `negocios_${new Date().toISOString().split('T')[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.log('Erro ao exportar negócios:', error);
        } finally {
            setLoadingExportExcel(false);
        }
    }

    // --- etapas: volume (conversaoPorEtapa) + aging (tempoPorEtapa)
    // As barras normalizam pelo MAIOR estágio, não pelo primeiro: em funis reais
    // o volume não é monotônico (histórico, estágios pulados), e normalizar pelo
    // primeiro estoura tudo para 100% quando ele é pequeno.
    const etapas = useMemo(() => {
        const volumes = comercial?.conversaoPorEtapa || [];
        const aging = new Map((comercial?.tempoPorEtapa || []).map((e) => [e.nome, e]));
        const maxTotal = Math.max(1, ...volumes.map((v) => v.total || 0));
        return volumes.map((etapa, i) => {
            const proxima = volumes[i + 1];
            return {
                nome: etapa.nome,
                total: etapa.total,
                pct: ((etapa.total || 0) / maxTotal) * 100,
                conversao: proxima && etapa.total > 0 ? (proxima.total / etapa.total) * 100 : null,
                aging: aging.get(etapa.nome)?.avgDias ?? null,
                cor: CORES_ETAPA[i % CORES_ETAPA.length],
            };
        });
    }, [comercial]);

    const gargalo = executivo?.gargalo || null;
    const maxOrigem = useMemo(() => Math.max(1, ...origens.map((o) => o.total || 0)), [origens]);
    const maxLeadsUsuario = useMemo(() => Math.max(1, ...leadsPorUsuario.map((u) => u.total_leads || 0)), [leadsPorUsuario]);
    const maxMotivo = useMemo(() => Math.max(1, ...motivosPerdas.map((m) => m.total || 0)), [motivosPerdas]);

    const semOrigem = useMemo(() => {
        const total = origens.reduce((acc, o) => acc + (o.total || 0), 0);
        const vazia = origens.find((o) => !o.nome || /sem origem/i.test(o.nome));
        return vazia ? { total: vazia.total, pct: ratio(vazia.total, total) } : null;
    }, [origens]);

    // --- conversão entre estágios (série do funil configurado)
    const estagios = useMemo(() => {
        const maxTotal = Math.max(1, ...conversaoEstagios.map((e) => e.total || 0));
        return conversaoEstagios.map((e, i) => ({
            nome: e.nome,
            total: e.total || 0,
            porcentagem: e.porcentagem,
            pct: ((e.total || 0) / maxTotal) * 100,
            cor: CORES_ETAPA[i % CORES_ETAPA.length],
        }));
    }, [conversaoEstagios]);

    // --- ganhos x perdas por usuário (valores)
    // O backend devolve `perda` como valor NEGATIVO (multiplicado por -1, pensado
    // para um gráfico de barra divergente). Aqui a barra é de composição lado a
    // lado, então precisa da magnitude — sem isso a largura fica negativa (não
    // renderiza) e quem só tem perdas some do filtro.
    const resultadoUsuarios = useMemo(() => {
        const lista = (ganhosPerdas || [])
            .map((g) => ({ usuario: g.usuario || 'Sem responsável', ganho: g.ganho || 0, perda: Math.abs(g.perda || 0) }))
            .filter((g) => g.ganho > 0 || g.perda > 0)
            .sort((a, b) => b.ganho + b.perda - (a.ganho + a.perda))
            .slice(0, 8);
        const max = Math.max(1, ...lista.map((g) => g.ganho + g.perda));
        return lista.map((g) => ({ ...g, max }));
    }, [ganhosPerdas]);

    const totalGanho = totais?.totalGanho || 0;
    const totalPerdido = totais?.totalPerdido || 0;
    const somaResultado = totalGanho + totalPerdido;

    // --- negócios ganhos por período: [{ano_mes, <usuario>: n }]
    const serieGanhosPeriodo = useMemo(
        () =>
            [...(negociosUsuarios || [])]
                .sort((a, b) => parseAnoMes(a.ano_mes).valueOf() - parseAnoMes(b.ano_mes).valueOf())
                .map((linha) => ({ ...linha, label: rotuloAnoMes(linha.ano_mes) })),
        [negociosUsuarios]
    );
    const seriesGanhosPeriodo = useMemo(() => {
        const chaves = Object.keys(negociosUsuarios?.[0] || {}).filter((k) => k !== 'ano_mes');
        return chaves.map((k, i) => ({ key: k, label: k, color: CORES_ETAPA[i % CORES_ETAPA.length] }));
    }, [negociosUsuarios]);

    const faixasValor = useMemo(
        () => (comercial?.distribuicaoFaixaValor || []).map((f) => ({ label: f.faixa, count: f.count || 0 })),
        [comercial]
    );

    const forecast = useMemo(() => {
        const provavel = comercial?.forecastProvavel || 0;
        const conservador = comercial?.forecastConservador || 0;
        const max = Math.max(1, provavel, conservador);
        return { provavel, conservador, max };
    }, [comercial]);

    const agentes = useMemo(() => {
        const lista = (conversaoPorAgente || []).filter((a) => Number(a.taxa) >= 0).slice(0, 10);
        const max = Math.max(1, ...lista.map((a) => Number(a.taxa) || 0));
        return lista.map((a) => ({ nome: a.nome || 'Sem nome', taxa: Number(a.taxa) || 0, max }));
    }, [conversaoPorAgente]);

    const envelhecidos = comercial?.negociosEnvelhecidos ?? 0;
    const winRate = comercial?.winRate ?? null;
    const cicloMedio = comercial?.cicloMedio ?? null;

    return (
        <div className="flex flex-col gap-4">
            <PageHead
                title="Funil de vendas"
                subtitle={
                    loading
                        ? 'carregando…'
                        : `${configCrm?.pipelines?.find((p) => p._id === pipelineId)?.nome || 'Funil'} · ${fmtInt(
                              executivo?.leadsCriados ?? 0
                          )} negócios criados no período`
                }
            >
                {(configCrm?.pipelines?.length || 0) > 1 && (
                    <Select
                        value={pipelineId}
                        onValueChange={(value) => {
                            setPipelineId(value);
                            carregar(value);
                        }}
                    >
                        <SelectTrigger className="min-h-[32px] h-8 w-auto gap-2 border-[#e2e2ea] rounded-lg bg-white text-[12.5px] font-semibold text-[#6b6b7b] focus:ring-0 focus:ring-offset-0">
                            <SelectValue placeholder="Funil" />
                        </SelectTrigger>
                        <SelectContent className="z-[99999]">
                            {configCrm.pipelines.map((item) => (
                                <SelectItem key={item._id} value={item._id}>
                                    {item.nome}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}

                <Popover>
                    <PopoverTrigger asChild>
                        <button type="button" className={D.ghostButton}>
                            Mais filtros
                            {filtrosAtivos > 0 && (
                                <span
                                    className="flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold"
                                    style={{ background: T.brandSoft, color: T.brand }}
                                >
                                    {filtrosAtivos}
                                </span>
                            )}
                        </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[520px] p-4" align="end">
                        <div className="grid grid-cols-2 gap-5">
                            <div className="space-y-3">
                                <h4 className="text-[13px] font-bold pb-2" style={{ color: T.text, borderBottom: `1px solid ${T.divider}` }}>
                                    Filtros do lead
                                </h4>
                                {podeVerTodosLeads && (
                                    <div>
                                        <label className="text-[11px] font-extrabold uppercase tracking-[0.04em] mb-1.5 block" style={{ color: T.muted }}>
                                            Visualização
                                        </label>
                                        <Select
                                            value={responsaveis}
                                            onValueChange={(value) => {
                                                setUsuarioSelected(null);
                                                setResponsaveis(value);
                                            }}
                                        >
                                            <SelectTrigger className="h-9 text-xs w-full">
                                                <SelectValue placeholder="Visualização" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="me">Meus leads</SelectItem>
                                                <SelectItem value="all">Todos os leads</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                {podeVerTodosLeads && (
                                    <div>
                                        <label className="text-[11px] font-extrabold uppercase tracking-[0.04em] mb-1.5 block" style={{ color: T.muted }}>
                                            Por usuário
                                        </label>
                                        <Select
                                            value={usuarioSelected || 'clear'}
                                            onValueChange={(value) => setUsuarioSelected(value === 'clear' ? null : value)}
                                        >
                                            <SelectTrigger className="h-9 text-xs w-full">
                                                <SelectValue placeholder="Selecione um usuário" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="clear">Limpar filtro</SelectItem>
                                                {listaUsuarios.map((item) => (
                                                    <SelectItem key={item._id} value={item._id}>
                                                        {item.nomeFantasia}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                <div>
                                    <label className="text-[11px] font-extrabold uppercase tracking-[0.04em] mb-1.5 block" style={{ color: T.muted }}>
                                        Qualificação
                                    </label>
                                    <Select value={qualificacao} onValueChange={setQualificacao}>
                                        <SelectTrigger className="h-9 text-xs w-full">
                                            <SelectValue placeholder="Qualificação" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Todas as qualificações</SelectItem>
                                            {configCrm?.qualificacoes?.map((item) => (
                                                <SelectItem key={item.value} value={item.value}>
                                                    {item.nome}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="space-y-3 pl-5" style={{ borderLeft: `1px solid ${T.divider}` }}>
                                <h4 className="text-[13px] font-bold pb-2" style={{ color: T.text, borderBottom: `1px solid ${T.divider}` }}>
                                    Filtros do chat
                                </h4>
                                <div>
                                    <label className="text-[11px] font-extrabold uppercase tracking-[0.04em] mb-1.5 block" style={{ color: T.muted }}>
                                        Tags do chat
                                    </label>
                                    <MultiSelect
                                        options={listaChatTags.map((tag) => ({ value: tag._id, label: tag.nome }))}
                                        defaultValue={chatTags}
                                        onValueChange={setChatTags}
                                        placeholder="Selecione tags"
                                        variant="default"
                                        maxCount={2}
                                        className="text-xs"
                                    />
                                    <p className="text-[11px] mt-1.5" style={{ color: T.empty }}>
                                        Filtra leads pelo chat vinculado.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </PopoverContent>
                </Popover>

                <button type="button" className={D.ghostButton} onClick={exportarExcel} disabled={loadingExportExcel || !pipelineId}>
                    {loadingExportExcel && <Iconify icon="mdi:loading" className="animate-spin h-3.5 w-3.5" />}
                    Exportar Excel
                </button>
            </PageHead>

            <div className={D.grid3}>
                <KpiCard label="Pipeline em aberto" value={fmtBRL(comercial?.pipelineAberto?.valor)} loading={loadingComercial}>
                    <div className={D.note}>
                        {fmtInt(comercial?.pipelineAberto?.qty ?? 0)} negócios · ticket médio {fmtBRL(comercial?.ticketMedio) || '—'}
                    </div>
                </KpiCard>

                <KpiCard
                    label="Win rate"
                    tone={winRate === null ? 'neutral' : winRate >= 20 ? 'ok' : 'neutral'}
                    value={fmtPct(winRate)}
                    loading={loadingComercial}
                >
                    <div className={D.note}>
                        {fmtInt(comercial?.ganhos?.qty ?? 0)} ganhos de {fmtInt((comercial?.ganhos?.qty ?? 0) + (comercial?.perdidos?.qty ?? 0))}{' '}
                        negócios fechados
                    </div>
                </KpiCard>

                <KpiCard
                    label="Ciclo médio"
                    tone={envelhecidos > 0 ? 'warn' : 'neutral'}
                    badge={envelhecidos > 0 ? 'ATENÇÃO' : null}
                    value={cicloMedio ? `${cicloMedio.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} dias` : null}
                    loading={loadingComercial}
                >
                    <div className={D.note}>
                        {envelhecidos > 0
                            ? `${fmtInt(envelhecidos)} negócios abertos há mais de 1,5× o ciclo`
                            : 'nenhum negócio envelhecido no funil'}
                    </div>
                </KpiCard>
            </div>

            <StatStrip
                loading={loading}
                items={[
                    { label: 'Negócios criados', value: fmtInt(executivo?.leadsCriados ?? 0), note: 'no período' },
                    {
                        label: 'Em aberto',
                        value: fmtInt(totais?.quantidade_abertos?.total_abertos ?? comercial?.pipelineAberto?.qty ?? 0),
                        note: 'ainda no funil',
                    },
                    {
                        label: 'Finalizados',
                        value: fmtInt(totais?.quantidade_finalizados?.total_finalizados ?? 0),
                        note: 'ganhos + perdidos',
                    },
                    {
                        label: 'Média de duração',
                        value:
                            totais?.media_duracao?.media_duracao !== undefined
                                ? `${Number(totais.media_duracao.media_duracao).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} d`
                                : null,
                        note: 'criação → fechamento',
                    },
                ]}
            />

            <StatStrip
                loading={loading}
                items={[
                    { label: 'Qualificados', value: fmtInt(executivo?.leadsQualificados ?? 0), note: 'passaram da 1ª etapa' },
                    { label: 'Perdidos', value: fmtInt(executivo?.leadsPerdidos ?? 0), note: 'negócios' },
                    { label: 'Conversão', value: fmtPct(executivo?.taxaConversao), note: 'ganhos / fechados' },
                    {
                        label: 'Negócios envelhecidos',
                        value: fmtInt(envelhecidos),
                        note: 'acima de 1,5× o ciclo',
                        tone: envelhecidos > 0 ? 'warn' : 'neutral',
                    },
                ]}
            />

            <StatStrip
                loading={loadingComercial}
                items={[
                    { label: 'Ganhos', value: fmtBRL(comercial?.ganhos?.valor), tone: 'ok', note: `${fmtInt(comercial?.ganhos?.qty ?? 0)} negócios` },
                    { label: 'Perdidos', value: fmtBRL(comercial?.perdidos?.valor), note: `${fmtInt(comercial?.perdidos?.qty ?? 0)} negócios` },
                    { label: 'Receita potencial', value: fmtBRL(executivo?.receitaPotencial), note: 'leads em aberto' },
                    { label: 'Ticket médio', value: fmtBRL(comercial?.ticketMedio), note: 'dos negócios ganhos' },
                ]}
            />

            <Block
                className="mt-4"
                title="Etapas do funil"
                subtitle="Negócios na etapa · conversão para a seguinte · tempo médio parado"
                bodyClassName="mt-3.5"
                insight={
                    gargalo ? (
                        <>
                            <Strong>{gargalo.estagio}</Strong> retém {fmtInt(gargalo.count)} negócios por{' '}
                            {Number(gargalo.avgAge).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} dias em média — é o gargalo do
                            funil.
                        </>
                    ) : null
                }
            >
                {loadingComercial ? (
                    <SkeletonBar height={220} />
                ) : !etapas.length ? (
                    <Empty>Nenhuma etapa configurada neste funil.</Empty>
                ) : (
                    <>
                        <ConversionFunnel
                            steps={etapas.map((e) => ({
                                label: e.nome,
                                value: e.total,
                                color: gargalo && gargalo.estagio === e.nome ? T.warnFill : e.cor,
                                conversao: e.conversao,
                            }))}
                        />
                        <div
                            className="grid gap-3 pt-3.5 pb-2.5 mt-5 text-[11px] font-extrabold uppercase tracking-[0.04em]"
                            style={{ gridTemplateColumns: COLS, color: T.muted, borderBottom: `1px solid ${T.divider}` }}
                        >
                            <div>Etapa</div>
                            <div>Volume</div>
                            <div className="text-right">Conv.</div>
                            <div className="text-right">Aging</div>
                        </div>
                        {etapas.map((etapa) => {
                            const ehGargalo = gargalo && gargalo.estagio === etapa.nome;
                            return (
                                <div
                                    key={etapa.nome}
                                    className="grid gap-3 items-center py-3.5"
                                    style={{
                                        gridTemplateColumns: COLS,
                                        borderBottom: `1px solid ${T.dividerSoft}`,
                                        background: ehGargalo ? '#fffbf3' : undefined,
                                    }}
                                >
                                    <div className="text-[13px] font-bold truncate" style={{ color: T.text, paddingLeft: ehGargalo ? 8 : 0 }}>
                                        {etapa.nome}
                                    </div>
                                    <div className="flex items-center gap-2.5">
                                        <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: T.track }}>
                                            <div
                                                className="h-full rounded-full"
                                                style={{
                                                    width: `${Math.max(0, Math.min(100, etapa.pct))}%`,
                                                    background: ehGargalo ? T.warnFill : etapa.cor,
                                                }}
                                            />
                                        </div>
                                        <span className="w-10 text-right text-[13px] font-extrabold tabular-nums">{fmtInt(etapa.total)}</span>
                                    </div>
                                    <div
                                        className="text-right text-[12.5px] tabular-nums"
                                        style={ehGargalo ? { color: T.warn, fontWeight: 800 } : { fontWeight: 700 }}
                                    >
                                        {etapa.conversao === null ? <Dash /> : fmtPct(etapa.conversao, 0)}
                                    </div>
                                    <div
                                        className="text-right text-[12.5px] tabular-nums"
                                        style={ehGargalo ? { color: T.warn, fontWeight: 700, paddingRight: 8 } : { color: T.label, fontWeight: 600 }}
                                    >
                                        {etapa.aging === null ? (
                                            <Dash />
                                        ) : (
                                            `${Number(etapa.aging).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} d`
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}
            </Block>

            {/* Os três cards curtos ficam lado a lado: como sidebar de 380px eles
                terminavam cedo e deixavam ~270px de vazio, enquanto o bloco mais denso
                (funil + tabela de N etapas) ficava espremido na coluna estreita. */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
                <div
                    className={D.card}
                    style={{ borderColor: gargalo ? T.warnBorder : T.border, padding: '20px 22px' }}
                >
                    <div className={D.kpiLabel}>Gargalo atual</div>
                    {loading ? (
                        <SkeletonBar height={26} />
                    ) : !gargalo ? (
                        <div className="mt-3 text-[13px] font-medium" style={{ color: T.empty }}>
                            Nenhum gargalo identificado no funil.
                        </div>
                    ) : (
                        <>
                            <div className="mt-2.5 text-[20px] font-extrabold tracking-[-0.02em]" style={{ color: T.warn }}>
                                {gargalo.estagio}
                            </div>
                            <div className="flex gap-6 mt-3.5">
                                <div>
                                    <div className={D.note}>Negócios</div>
                                    <div className="text-[18px] font-extrabold tabular-nums">{fmtInt(gargalo.count)}</div>
                                </div>
                                <div>
                                    <div className={D.note}>Idade média</div>
                                    <div className="text-[18px] font-extrabold tabular-nums">
                                        {Number(gargalo.avgAge).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} d
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <Block title="Negócios por origem" bodyClassName="mt-4">
                    {loading ? (
                        <SkeletonBar height={120} />
                    ) : !origens.length ? (
                        <Empty>Nenhum negócio com origem no período.</Empty>
                    ) : (
                        <>
                            <div className="flex flex-col gap-3">
                                {origens.slice(0, 6).map((o, i) => {
                                    const vazia = !o.nome || /sem origem/i.test(o.nome);
                                    return (
                                        <BarRow
                                            key={o.nome || `origem-${i}`}
                                            label={o.nome || 'Sem origem'}
                                            value={fmtInt(o.total)}
                                            pct={(o.total / maxOrigem) * 100}
                                            color={vazia ? T.warnFill : CORES_ETAPA[i % CORES_ETAPA.length]}
                                            height={8}
                                        />
                                    );
                                })}
                            </div>
                            {semOrigem?.pct ? (
                                <div className="mt-4 text-[11.5px] font-semibold" style={{ color: T.warn }}>
                                    {fmtPct(semOrigem.pct, 0)} dos negócios sem origem preenchida
                                </div>
                            ) : null}
                        </>
                    )}
                </Block>

                <Block title="Criados por usuário" bodyClassName="mt-4">
                    {loading ? (
                        <SkeletonBar height={120} />
                    ) : !leadsPorUsuario.length ? (
                        <Empty>Nenhum negócio criado no período.</Empty>
                    ) : (
                        <div className="flex flex-col gap-[11px]">
                            {leadsPorUsuario.slice(0, 8).map((u) => (
                                <div key={u.usuario_nome} className="flex items-center gap-2.5">
                                    <span className="w-[74px] text-[12.5px] font-semibold truncate" style={{ color: T.text2 }}>
                                        {u.usuario_nome}
                                    </span>
                                    <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: T.track }}>
                                        <div
                                            className="h-full rounded-full"
                                            style={{ width: `${((u.total_leads || 0) / maxLeadsUsuario) * 100}%`, background: T.brand }}
                                        />
                                    </div>
                                    <span className="w-7 text-right text-[12.5px] font-bold tabular-nums">{fmtInt(u.total_leads)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </Block>
            </div>

            <div className={D.grid2}>
                <Block title="Conversão por estágio" subtitle="Volume por estágio e conversão para o seguinte" bodyClassName="mt-[18px]">
                    {loading ? (
                        <SkeletonBar height={160} />
                    ) : !estagios.length ? (
                        <Empty>Nenhum estágio com negócios no período.</Empty>
                    ) : (
                        <ConversionFunnel
                            steps={estagios.map((e) => ({ label: e.nome, value: e.total, color: e.cor, conversao: e.porcentagem }))}
                        />
                    )}
                </Block>

                <Block title="Motivos de perda" bodyClassName="mt-[18px]">
                    {loading ? (
                        <SkeletonBar height={160} />
                    ) : !motivosPerdas.length ? (
                        <Empty>Nenhum negócio perdido com motivo registrado.</Empty>
                    ) : (
                        <div className="flex flex-col gap-3.5">
                            {motivosPerdas.slice(0, 6).map((m, i) => (
                                <BarRow
                                    key={m.motivo || i}
                                    label={m.motivo || 'Sem motivo'}
                                    value={fmtInt(m.total)}
                                    pct={((m.total || 0) / maxMotivo) * 100}
                                    color={T.critFill}
                                    height={8}
                                />
                            ))}
                        </div>
                    )}
                </Block>
            </div>

            <div className={D.grid2}>
                <Block
                    title="Ganhos x perdas por usuário"
                    subtitle="Valor fechado no período"
                    right={
                        <Legend
                            items={[
                                { color: T.okFill, label: 'Ganho' },
                                { color: T.critFill, label: 'Perda' },
                            ]}
                        />
                    }
                >
                    {loading ? (
                        <SkeletonBar height={160} />
                    ) : !resultadoUsuarios.length ? (
                        <Empty>Nenhum negócio fechado no período.</Empty>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {resultadoUsuarios.map((g) => (
                                <div key={g.usuario} className="flex items-center gap-3">
                                    <span className="w-24 text-[12.5px] font-semibold truncate" style={{ color: T.text2 }}>
                                        {g.usuario}
                                    </span>
                                    <div className="flex-1 flex h-2.5 rounded-full overflow-hidden" style={{ background: T.track }}>
                                        <div style={{ width: `${(g.ganho / g.max) * 100}%`, background: T.okFill }} />
                                        <div style={{ width: `${(g.perda / g.max) * 100}%`, background: T.critFill }} />
                                    </div>
                                    <span className="w-16 text-right text-[12px] font-bold tabular-nums" style={{ color: T.ok }}>
                                        {fmtBRL(g.ganho)}
                                    </span>
                                    <span className="w-16 text-right text-[12px] font-bold tabular-nums" style={{ color: T.crit }}>
                                        {g.perda > 0 ? fmtBRL(g.perda) : <Dash />}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </Block>

                <Block
                    title="Total ganho x perdido"
                    subtitle="Valor consolidado do período"
                    bodyClassName="mt-[18px]"
                    insight={
                        somaResultado > 0 ? (
                            <>
                                <Strong>{fmtPct(ratio(totalGanho, somaResultado), 0)}</Strong> do valor fechado veio de negócios ganhos.
                            </>
                        ) : null
                    }
                >
                    {loading ? (
                        <SkeletonBar height={120} />
                    ) : somaResultado === 0 ? (
                        <Empty>Nenhum valor fechado no período.</Empty>
                    ) : (
                        <>
                            <Stacked
                                height={34}
                                radius={8}
                                showLabels
                                segments={[
                                    { pct: (totalGanho / somaResultado) * 100, color: T.okFill, label: 'Ganho' },
                                    { pct: (totalPerdido / somaResultado) * 100, color: T.critFill, label: 'Perdido' },
                                ]}
                            />
                            <div className="flex justify-between gap-4 mt-4">
                                <div>
                                    <div className={D.note}>Ganho</div>
                                    <div className="text-[20px] font-extrabold tabular-nums" style={{ color: T.ok }}>
                                        {fmtBRL(totalGanho)}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className={D.note}>Perdido</div>
                                    <div className="text-[20px] font-extrabold tabular-nums" style={{ color: T.crit }}>
                                        {fmtBRL(totalPerdido)}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </Block>
            </div>

            {!hidePannels?.includes('last_6_months') && (
                <Block title="Negócios ganhos por período" subtitle="Últimos 6 meses, por responsável">
                    {loading ? (
                        <SkeletonBar height={180} />
                    ) : !serieGanhosPeriodo.length ? (
                        <Empty>Nenhum negócio ganho nos últimos meses.</Empty>
                    ) : (
                        <>
                            <ColumnChart data={serieGanhosPeriodo} series={seriesGanhosPeriodo} height={180} />
                            <div className="mt-3.5">
                                <Legend items={seriesGanhosPeriodo.map((s) => ({ color: s.color, label: s.label }))} />
                            </div>
                        </>
                    )}
                </Block>
            )}

            <div className={D.grid2}>
                <Block title="Distribuição por faixa de valor" subtitle="Negócios criados no período">
                    {loadingComercial ? (
                        <SkeletonBar height={160} />
                    ) : !faixasValor.length ? (
                        <Empty>Nenhum negócio com valor informado no período.</Empty>
                    ) : (
                        <ColumnChart data={faixasValor} series={[{ key: 'count', label: 'Negócios', color: T.brand }]} height={160} />
                    )}
                </Block>

                <Block
                    title="Forecast"
                    subtitle="Projeção de fechamento a partir do pipeline aberto"
                    bodyClassName="mt-[18px]"
                    insight={
                        <>
                            <Strong>Provável</Strong> pondera cada estágio pela proximidade do fechamento;{' '}
                            <Strong>conservador</Strong> conta apenas os dois últimos estágios.
                        </>
                    }
                >
                    {loadingComercial ? (
                        <SkeletonBar height={120} />
                    ) : forecast.provavel === 0 && forecast.conservador === 0 ? (
                        <Empty>Sem pipeline aberto para projetar.</Empty>
                    ) : (
                        <div className="flex flex-col gap-3.5">
                            <BarRow
                                label="Provável"
                                value={fmtBRL(forecast.provavel)}
                                pct={(forecast.provavel / forecast.max) * 100}
                                color={T.brand}
                                height={8}
                            />
                            <BarRow
                                label="Conservador"
                                value={fmtBRL(forecast.conservador)}
                                pct={(forecast.conservador / forecast.max) * 100}
                                color={T.brand3}
                                height={8}
                            />
                        </div>
                    )}
                </Block>
            </div>

            <Block title="Conversão por agente" subtitle="Taxa de conversão de leads por responsável" bodyClassName="mt-[18px]">
                {loading ? (
                    <SkeletonBar height={160} />
                ) : !agentes.length ? (
                    <Empty>Nenhum agente com leads no período.</Empty>
                ) : (
                    <div className="flex flex-col gap-3">
                        {agentes.map((a) => (
                            <div key={a.nome} className="flex items-center gap-3">
                                <span className="w-28 text-[12.5px] font-semibold truncate" style={{ color: T.text2 }}>
                                    {a.nome}
                                </span>
                                <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: T.track }}>
                                    <div className="h-full rounded-full" style={{ width: `${(a.taxa / a.max) * 100}%`, background: T.brand }} />
                                </div>
                                <span className="w-12 text-right text-[12.5px] font-bold tabular-nums">{fmtPct(a.taxa, 0)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </Block>
        </div>
    );
}
